import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Target, Zap, Star, ArrowRight, Home, RefreshCw, Users, X, ClipboardCheck } from 'lucide-react';
import { type ModuleData, type ModuleId, getFamilyDefinition } from '../../content';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAppStore } from '../../app/hooks/useAppStore';
import { SESSION_CONFIG } from '../../config/constants';
import { useDjaySelection, getDjayGreeting, DjayAvatar } from '../../mascot';
import type { DjayEmotion } from '../../mascot';
import { APP_NAME } from '../../config/constants';
import { DjayRecommendationCard } from './DjayRecommendationCard';
import { PageHero, HeroStatPill } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { AssignedAssessmentBanner } from './AssignedAssessmentBanner';
import { StreakFlame } from '../shared/StreakFlame';
import { dataLayer } from '../../data/dataLayer';
import type { AssessmentConfig } from '../../assessment';
import { DOMAIN_MODULE_MAP } from '../../assessment/utils/assessmentDomainConfig';
import { soundManager } from '../../utils/soundManager';
import { getWeeklyActivityForLearner, type DayActivity } from '../../utils/activityHelpers';
import { WeeklyActivityDots } from '../shared/WeeklyActivityDots';
import { hasActiveDecay } from '../../mastery/masteryDecay';
import { getWeakestFamiliesGlobal, getDjayRecommendation } from '../../mastery';
import { getTodayChallenge } from '../../utils/dailyChallenge';
import { DailyChallengeCard } from './DailyChallengeCard';
interface HomePageProps {
  modules: ModuleData[];
  state: AppState;
  selectedGroup: GroupId;
  displayName: string;
  onStartPractice: (moduleId: ModuleId) => void;
  onNavigateToModules: () => void;
  onModuleSelect: (module: ModuleData) => void;
  onNavigateToAssessment: () => void;
  onStartAssessment: (config: AssessmentConfig) => void;
  onNavigateToLab?: () => void;
}
export function HomePage({
  modules,
  state,
  selectedGroup,
  displayName,
  onStartPractice,
  onNavigateToModules,
  onModuleSelect,
  onNavigateToAssessment,
  onStartAssessment,
  onNavigateToLab
}: HomePageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    accounts,
    activeAccount
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const [activeAssignments, setActiveAssignments] = useState<any[]>([]);
  const [showKlascodeBanner, setShowKlascodeBanner] = useState(false);
  // Check if learner has no classroom and banner not dismissed
  useEffect(() => {
    async function checkKlascodeBanner() {
      if (!activeAccount || activeAccount.role !== 'learner') {
        setShowKlascodeBanner(false);
        return;
      }
      // Check if banner was dismissed
      const dismissed = await dataLayer.getKlascodeBannerDismissed();
      if (dismissed) {
        setShowKlascodeBanner(false);
        return;
      }
      // Check if learner is in any classroom
      const hasClassroom = accounts && Object.values(accounts.classrooms || {}).some(classroom => classroom.learnerIds.includes(activeAccount.id));
      setShowKlascodeBanner(!hasClassroom);
    }
    checkKlascodeBanner();
  }, [activeAccount, accounts]);
  const handleDismissKlascodeBanner = async () => {
    await dataLayer.setKlascodeBannerDismissed(true);
    setShowKlascodeBanner(false);
  };
  useEffect(() => {
    async function loadAssignments() {
      if (!activeAccount) return;
      const all = await dataLayer.getTeacherAssignments();
      const now = new Date();
      const valid = Object.values(all).filter((assignment: any) => {
        if (assignment.group !== selectedGroup) return false;
        if (assignment.deadline && new Date(assignment.deadline) < now) return false;
        const completedBy = assignment.completedByStudentIds as string[] || [];
        if (completedBy.includes(activeAccount.id)) return false;
        if (assignment.wholeClass) return true;
        if (assignment.targetStudentIds && assignment.targetStudentIds.includes(activeAccount.id)) return true;
        return false;
      });
      setActiveAssignments(valid);
    }
    loadAssignments();
  }, [activeAccount, selectedGroup]);
  const hour = new Date().getHours();
  let greeting = 'Hallo';
  if (hour < 8) greeting = 'Goedemorgen';else if (hour < 12) greeting = 'Goedemorgen';else if (hour < 18) greeting = 'Goedemiddag';else greeting = 'Goedenavond';
  const djayEmotion: DjayEmotion = useMemo(() => getDjayGreeting(displayName).emotion,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [hour]);
  const currentWeekDays = state.dailyStreak?.currentWeekDays ?? 0;
  const currentStreak = currentWeekDays;
  const todayQuestionsAnswered = state.dailyStreak?.todayQuestionsAnswered ?? 0;
  const dailyGoal = state.dailyStreak?.dailyGoalTarget ?? SESSION_CONFIG.DAILY_GOAL;
  const weeklyGoal = state.dailyStreak?.weeklyGoalTarget ?? 3;
  // Dynamic subtitle based on daily progress
  const dynamicSubtitle = useMemo(() => {
    const remaining = dailyGoal - todayQuestionsAnswered;
    if (todayQuestionsAnswered >= dailyGoal) {
      return 'Je dagdoel is gehaald! 🎉';
    } else if (todayQuestionsAnswered > 0) {
      return `Nog ${remaining} ${remaining === 1 ? 'vraag' : 'vragen'} voor je dagdoel!`;
    } else {
      return `Klaar voor een nieuw avontuur in groep ${selectedGroup}? 🚀`;
    }
  }, [todayQuestionsAnswered, dailyGoal, selectedGroup]);
  const recommendation = useMemo(() => {
    return getDjayRecommendation(state.familyMastery || {}, modules, selectedGroup, getModuleMasteryForGroup, getAnsweredQuestions);
  }, [state.familyMastery, modules, selectedGroup, getModuleMasteryForGroup, getAnsweredQuestions]);
  const recommendedModule = useMemo(() => {
    return recommendation ? modules.find(m => m.id === recommendation.moduleId) : null;
  }, [recommendation, modules]);
  const hasStarted = modules.some(m => {
    const answered = getAnsweredQuestions(m.id).length;
    return answered > 0;
  });
  // Find last practiced module
  const lastPracticedModule = useMemo(() => {
    if (!state.questionStates) return null;
    let latestTime = 0;
    let latestModuleId: ModuleId | null = null;
    // We can iterate over modules and find the max lastAttemptAt
    modules.forEach(m => {
      const answered = getAnsweredQuestions(m.id);
      answered.forEach(q => {
        if (q.lastAttemptAt && q.lastAttemptAt > latestTime) {
          latestTime = q.lastAttemptAt;
          latestModuleId = m.id;
        }
      });
    });
    return latestModuleId ? modules.find(m => m.id === latestModuleId) : null;
  }, [modules, getAnsweredQuestions, state.questionStates]);
  // Get decaying families
  const decayingFamilies = useMemo(() => {
    if (!state.familyMastery) return [];
    const weakest = getWeakestFamiliesGlobal(state.familyMastery, selectedGroup, 10);
    return weakest.filter(f => hasActiveDecay(state.familyMastery?.[f.key])).slice(0, 2);
  }, [state.familyMastery, selectedGroup]);
  // Weekly activity
  const weeklyActivity = useMemo(() => {
    return getWeeklyActivityForLearner(state.questionStates || {});
  }, [state.questionStates]);
  // Daily Challenge
  const dailyChallenge = useMemo(() => {
    return getTodayChallenge(state, modules, selectedGroup);
  }, [state, modules, selectedGroup]);
  const handleStartChallenge = () => {
    if (dailyChallenge.moduleId) {
      onStartPractice(dailyChallenge.moduleId);
    } else {
      onNavigateToModules();
    }
  };
  return <PageContainer className="space-y-8 pb-20" data-id="element-1197">
      {/* HERO ZONE */}
      <motion.div variants={itemVariants} data-id="element-1198">
        {/* Mobile Compact Hero */}
        <div className="md:hidden" data-id="element-1199">
          <Card className="p-4 bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500 border-none shadow-md text-white flex items-center gap-4 rounded-2xl relative overflow-hidden" data-id="element-1200">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4" data-id="element-1201" />
            <div className="flex-shrink-0 relative z-10" data-id="element-1202">
              <DjayAvatar size="sm" emotion={djayEmotion} variant={selectedVariant} animate data-id="element-1203" />
            </div>
            <div className="flex-1 relative z-10" data-id="element-1204">
              <h1 className="text-xl font-black tracking-tight mb-0.5" data-id="element-1205">
                {greeting}, {displayName}!
              </h1>
              <p className="text-white/90 text-xs font-medium" data-id="element-1206">
                {dynamicSubtitle}
              </p>
            </div>
          </Card>
        </div>

        {/* Desktop Full Hero */}
        <div className="hidden md:block" data-id="element-1207">
          <PageHero title={`${greeting}, ${displayName}!`} subtitle={dynamicSubtitle} gradientClasses="from-orange-500 via-amber-500 to-yellow-500" djayEmotion={djayEmotion} djayVariant={selectedVariant} icon={Home} iconBgClass="bg-blue-400" floatingEmojis={['🚀', '⭐']} data-id="element-1208">
            <HeroStatPill icon={Star} data-id="element-1209">Level {state.stats.level}</HeroStatPill>
            <HeroStatPill icon={Target} data-id="element-1210">
              {todayQuestionsAnswered}/{dailyGoal} dagdoel
            </HeroStatPill>
            <HeroStatPill icon={() => <div className="flex items-center justify-center -ml-1 -mr-1" data-id="element-1212">
                  <StreakFlame streak={currentStreak} size="sm" data-id="element-1213" />
                </div>} data-id="element-1211">
              {currentStreak}/{weeklyGoal} weekdagen
            </HeroStatPill>
          </PageHero>
        </div>
      </motion.div>

      {/* KLASCODE BANNER */}
      {showKlascodeBanner && <motion.div variants={itemVariants} data-id="element-1214">
          <Card className="p-3 sm:p-4 border-2 border-amber-200 bg-amber-50 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 relative overflow-hidden shadow-sm" data-id="element-1215">
            <div className="flex items-center gap-3" data-id="element-1216">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0 shadow-sm" data-id="element-1217">
                <Users className="w-5 h-5" data-id="element-1218" />
              </div>
              <div data-id="element-1219">
                <h3 className="text-sm font-bold text-amber-900" data-id="element-1220">
                  Heb je een klascode?
                </h3>
                <p className="text-xs text-amber-700 font-medium" data-id="element-1221">
                  Je kunt deze op elk moment invullen via Instellingen.
                </p>
              </div>
            </div>
            <button onClick={handleDismissKlascodeBanner} className="absolute top-2 right-2 sm:static p-1.5 text-amber-500 hover:bg-amber-100 rounded-lg transition-colors" title="Sluiten" data-id="element-1222">
              <X className="w-4 h-4" data-id="element-1223" />
            </button>
          </Card>
        </motion.div>}

      {/* ACTIVE ASSIGNMENTS */}
      {activeAssignments.length > 0 && <motion.div variants={itemVariants} data-id="element-1224">
          <Card className="p-4 border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm" data-id="element-1225">
            <div className="flex items-center gap-3" data-id="element-1226">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0 shadow-sm" data-id="element-1227">
                <ClipboardCheck className="w-5 h-5" data-id="element-1228" />
              </div>
              <div data-id="element-1229">
                <h3 className="text-sm font-bold text-indigo-900" data-id="element-1230">
                  {activeAssignments.length}{' '}
                  {activeAssignments.length === 1 ? 'toets staat' : 'toetsen staan'}{' '}
                  voor je klaar!
                </h3>
                <p className="text-xs text-indigo-700 font-medium" data-id="element-1231">
                  Je leraar heeft nieuwe toetsen klaargezet.
                </p>
              </div>
            </div>
            <Button onClick={onNavigateToAssessment} size="sm" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" data-id="element-1232">
              Bekijk toetsen <ArrowRight className="w-4 h-4 ml-1.5" data-id="element-1233" />
            </Button>
          </Card>
        </motion.div>}

      {/* QUICK NAVIGATION */}
      <motion.div variants={itemVariants} className="grid grid-cols-3 gap-3 sm:gap-4" data-id="element-1234">
        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToModules();
      }} className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-blue-200 hover:bg-blue-50 transition-all group shadow-sm" data-id="element-1235">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1236">
            <Target className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-1237" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1238">
            Mijn Werelden
          </span>
        </button>

        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToLab();
      }} className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-purple-200 hover:bg-purple-50 transition-all group shadow-sm" data-id="element-1239">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1240">
            <Zap className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-1241" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1242">
            Oefenlab
          </span>
        </button>

        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToAssessment();
      }} className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-orange-200 hover:bg-orange-50 transition-all group shadow-sm" data-id="element-1243">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1244">
            <ClipboardCheck className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-1245" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1246">
            Toets
          </span>
        </button>
      </motion.div>

      {/* PRIMARY ACTION */}
      <motion.div variants={itemVariants} className="flex flex-col gap-4" data-id="element-1247">
        {hasStarted && lastPracticedModule && <ContinueCard module={lastPracticedModule} onStart={() => onStartPractice(lastPracticedModule.id)} djayVariant={selectedVariant} data-id="element-1248" />}
        {hasStarted && recommendedModule && recommendation ? <DjayRecommendationCard module={recommendedModule} recommendation={recommendation} onStart={() => onStartPractice(recommendedModule.id)} djayVariant={selectedVariant} data-id="element-1249" /> : !hasStarted && <WelcomeCard onStart={onNavigateToModules} djayVariant={selectedVariant} data-id="element-1250" />}
      </motion.div>

      {/* DAILY CHALLENGE */}
      {hasStarted && <motion.div variants={itemVariants} data-id="element-1251">
          <DailyChallengeCard challenge={dailyChallenge} djayVariant={selectedVariant} onStart={handleStartChallenge} data-id="element-1252" />
        </motion.div>}

      {/* DAGDOEL & STREAK ROW */}
      <motion.div variants={itemVariants} data-id="element-1253">
        {/* Mobile: single compact row */}
        <div className="md:hidden" data-id="element-1254">
          <Card className="p-4 border-2 border-amber-100 bg-white rounded-2xl shadow-md" data-id="element-1255">
            <div className="flex items-center justify-between gap-4" data-id="element-1256">
              {/* Daily Goal */}
              <div className="flex items-center gap-3" data-id="element-1257">
                <div className="relative" style={{
                width: 60,
                height: 60
              }} data-id="element-1258">
                  <svg width={60} height={60} viewBox="0 0 60 60" className="transform -rotate-90" data-id="element-1259">
                    <circle cx={30} cy={30} r={25} fill="none" stroke="#FEF3C7" strokeWidth={6} data-id="element-1260" />
                    <motion.circle initial={{
                    strokeDashoffset: 157
                  }} animate={{
                    strokeDashoffset: 157 - Math.min(100, todayQuestionsAnswered / dailyGoal * 100) / 100 * 157
                  }} transition={{
                    duration: 1.5,
                    ease: 'easeOut'
                  }} cx={30} cy={30} r={25} fill="none" stroke={todayQuestionsAnswered >= dailyGoal ? '#10B981' : '#F59E0B'} strokeWidth={6} strokeDasharray={157} strokeLinecap="round" data-id="element-1261" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-1262">
                    <span className="text-lg font-black text-slate-800 leading-none" data-id="element-1263">
                      {todayQuestionsAnswered}
                    </span>
                    <span className="text-[10px] font-bold text-slate-400" data-id="element-1264">
                      /{dailyGoal}
                    </span>
                  </div>
                </div>
                <div data-id="element-1265">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-1266">
                    Dagdoel
                  </div>
                  <div className="text-sm font-bold text-amber-600" data-id="element-1267">
                    {todayQuestionsAnswered >= dailyGoal ? 'Gehaald! 🎉' : 'Goed bezig!'}
                  </div>
                </div>
              </div>

              {/* Streak */}
              <div className="flex items-center gap-3" data-id="element-1268">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center shadow-sm" data-id="element-1269">
                  <StreakFlame streak={currentStreak} size="sm" data-id="element-1270" />
                </div>
                <div data-id="element-1271">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-1272">
                    Weekdoel
                  </div>
                  <div className="flex items-baseline gap-1" data-id="element-1273">
                    <span className="text-2xl font-black text-slate-800" data-id="element-1274">
                      {currentStreak}
                    </span>
                    <span className="text-xs font-bold text-slate-500" data-id="element-1275">
                      / {weeklyGoal} dagen
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Weekly activity dots */}
            <div className="mt-3 pt-3 border-t border-slate-100" data-id="element-1276">
              <WeeklyActivityDots weeklyActivity={weeklyActivity} color="emerald" weeklyGoal={weeklyGoal} data-id="element-1277" />
            </div>
          </Card>
        </div>

        {/* Desktop: two-column grid */}
        <div className="hidden md:grid grid-cols-2 gap-6" data-id="element-1278">
          <DailyGoalCard answered={todayQuestionsAnswered} goal={dailyGoal} data-id="element-1279" />
          <WeekStreakCard streak={currentStreak} weeklyActivity={weeklyActivity} weeklyGoal={weeklyGoal} data-id="element-1280" />
        </div>
      </motion.div>

      {/* HERHAAL TIP */}
      {decayingFamilies.length > 0 && <motion.div variants={itemVariants} className="flex flex-col gap-4" data-id="element-1281">
          {decayingFamilies.map(f => {
        const def = getFamilyDefinition(f.familyId);
        const title = def?.description || f.familyId;
        const module = modules.find(m => f.familyId.includes(m.id));
        return <Card key={f.familyId} className="p-4 sm:p-5 border-2 border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl cursor-pointer hover:shadow-lg transition-all group shadow-md" onClick={() => module && onStartPractice(module.id)} data-id="element-1282">
                <div className="flex items-center gap-3 sm:gap-4" data-id="element-1283">
                  <div className="flex-shrink-0" data-id="element-1284">
                    <DjayAvatar size="xs" emotion="thinking" variant={selectedVariant} animate data-id="element-1285" />
                  </div>
                  <div className="flex-1" data-id="element-1286">
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-1" data-id="element-1287">
                      <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-600" data-id="element-1288" />
                      <h3 className="text-sm sm:text-base font-black text-teal-900" data-id="element-1289">
                        Tijd voor een opfrisser!
                      </h3>
                    </div>
                    <p className="text-xs sm:text-sm text-teal-700 font-medium leading-relaxed" data-id="element-1290">
                      Je hebt <strong className="text-teal-900" data-id="element-1291">{title}</strong>{' '}
                      al een tijdje niet geoefend.
                    </p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-teal-200 flex items-center justify-center text-teal-700 group-hover:bg-teal-500 group-hover:text-white transition-colors flex-shrink-0 shadow-sm" data-id="element-1292">
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-1293" />
                  </div>
                </div>
              </Card>;
      })}
        </motion.div>}
    </PageContainer>;
}
function WelcomeCard({
  onStart,
  djayVariant
}: {
  onStart: () => void;
  djayVariant: any;
}) {
  return <Card className="p-6 sm:p-10 text-center bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-100 shadow-lg relative overflow-hidden rounded-[2rem]" data-id="element-1294">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-1295" />
      <div className="relative z-10 flex flex-col items-center" data-id="element-1296">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} className="mb-4 sm:mb-6" data-id="element-1297">
          <div className="block sm:hidden" data-id="element-1298">
            <DjayAvatar size="md" emotion="celebrating" variant={djayVariant} animate data-id="element-1299" />
          </div>
          <div className="hidden sm:block" data-id="element-1300">
            <DjayAvatar size="lg" emotion="celebrating" variant={djayVariant} animate data-id="element-1301" />
          </div>
        </motion.div>
        <h2 className="text-xl sm:text-3xl font-black text-slate-800 mb-2 sm:mb-3 tracking-tight" data-id="element-1302">
          Welkom bij {APP_NAME}!
        </h2>
        <p className="hidden sm:block text-slate-600 text-base sm:text-lg mb-8 max-w-md mx-auto font-medium leading-relaxed" data-id="element-1303">
          Je avontuur begint hier. Kies een wereld om te oefenen, verdien XP en
          bouw je streak op! 🚀
        </p>
        <Button onClick={onStart} variant="primary" size="lg" className="shadow-lg rounded-2xl sm:h-14 sm:text-lg sm:px-8 hover:shadow-xl transition-shadow" data-id="element-1304">
          <Star className="w-5 h-5 sm:w-6 sm:h-6 mr-2 fill-current" data-id="element-1305" />
          Kies je eerste wereld
        </Button>
      </div>
    </Card>;
}
function ContinueCard({
  module,
  onStart,
  djayVariant
}: {
  module: ModuleData;
  onStart: () => void;
  djayVariant: any;
}) {
  const colorMap: Record<string, string> = {
    'text-blue-500': 'from-blue-50 to-indigo-50 border-blue-200 text-blue-900 bg-blue-600 hover:bg-blue-700',
    'text-indigo-500': 'from-indigo-50 to-violet-50 border-indigo-200 text-indigo-900 bg-indigo-600 hover:bg-indigo-700',
    'text-orange-500': 'from-orange-50 to-amber-50 border-orange-200 text-orange-900 bg-orange-600 hover:bg-orange-700',
    'text-sky-500': 'from-sky-50 to-blue-50 border-sky-200 text-sky-900 bg-sky-600 hover:bg-sky-700',
    'text-green-500': 'from-green-50 to-emerald-50 border-green-200 text-green-900 bg-green-600 hover:bg-green-700',
    'text-rose-500': 'from-rose-50 to-pink-50 border-rose-200 text-rose-900 bg-rose-600 hover:bg-rose-700',
    'text-amber-500': 'from-amber-50 to-yellow-50 border-amber-200 text-amber-900 bg-amber-600 hover:bg-amber-700',
    'text-purple-500': 'from-purple-50 to-fuchsia-50 border-purple-200 text-purple-900 bg-purple-600 hover:bg-purple-700',
    'text-teal-500': 'from-teal-50 to-emerald-50 border-teal-200 text-teal-900 bg-teal-600 hover:bg-teal-700',
    'text-cyan-500': 'from-cyan-50 to-sky-50 border-cyan-200 text-cyan-900 bg-cyan-600 hover:bg-cyan-700'
  };
  const theme = colorMap[module.color] || colorMap['text-blue-500'];
  const [bgGradient, border, textColor, btnBg, btnHover] = theme.split(' ').reduce((acc, cls) => {
    if (cls.startsWith('from-')) acc[0] = `${cls} ${theme.split(' ')[1]}`;
    if (cls.startsWith('border-')) acc[1] = cls;
    if (cls.startsWith('text-')) acc[2] = cls;
    if (cls.startsWith('bg-')) acc[3] = cls;
    if (cls.startsWith('hover:bg-')) acc[4] = cls;
    return acc;
  }, ['', '', '', '', '']);
  return <Card className={`p-4 sm:p-6 border-2 ${border} bg-gradient-to-r ${bgGradient} rounded-2xl shadow-md relative overflow-hidden group cursor-pointer`} onClick={onStart} data-id="element-1306">
      <div className="absolute right-0 top-0 w-32 h-32 bg-white/40 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4" data-id="element-1307" />
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative z-10" data-id="element-1308">
        <div className="flex items-center gap-4" data-id="element-1309">
          <div className="flex-shrink-0" data-id="element-1310">
            <DjayAvatar size="sm" emotion="happy" variant={djayVariant} animate data-id="element-1311" />
          </div>
          <div data-id="element-1312">
            <h3 className={`text-xs font-bold ${textColor} uppercase tracking-wider mb-1 opacity-80`} data-id="element-1313">
              Ga verder waar je gebleven was
            </h3>
            <p className={`text-xl font-black ${textColor}`} data-id="element-1314">{module.title}</p>
            <div className="flex items-center gap-2 mt-2" data-id="element-1315">
              <div className="h-1.5 w-24 bg-white/60 rounded-full overflow-hidden" data-id="element-1316">
                <div className={`h-full ${btnBg} rounded-full w-2/3`} data-id="element-1317" />
              </div>
              <span className={`text-xs font-bold ${textColor} opacity-70`} data-id="element-1318">
                Lekker bezig!
              </span>
            </div>
          </div>
        </div>
        <Button onClick={e => {
        e.stopPropagation();
        onStart();
      }} size="lg" className={`${btnBg} ${btnHover} text-white shadow-sm rounded-xl w-full sm:w-auto transition-transform group-hover:scale-105`} data-id="element-1319">
          Ga verder <ArrowRight className="w-5 h-5 ml-2" data-id="element-1320" />
        </Button>
      </div>
    </Card>;
}
function DailyGoalCard({
  answered,
  goal
}: {
  answered: number;
  goal: number;
}) {
  const percentage = Math.min(100, Math.round(answered / goal * 100));
  const size = 100;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - percentage / 100 * circumference;
  let message = 'Begin je avontuur!';
  if (percentage >= 100) message = 'Dagdoel gehaald! 🎉';else if (percentage >= 50) message = 'Bijna daar!';else if (percentage > 0) message = 'Goed bezig!';
  return <Card className="p-6 border-2 border-amber-100 bg-white rounded-[2rem] flex flex-col items-center justify-center text-center relative overflow-hidden shadow-md hover:shadow-lg transition-shadow" data-id="element-1321">
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-amber-50 rounded-full blur-2xl" data-id="element-1322" />

      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 relative z-10" data-id="element-1323">
        Dagdoel
      </h3>

      <div className="relative mb-4" style={{
      width: size,
      height: size
    }} data-id="element-1324">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90" data-id="element-1325">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#FEF3C7" strokeWidth={strokeWidth} data-id="element-1326" />
          <motion.circle initial={{
          strokeDashoffset: circumference
        }} animate={{
          strokeDashoffset: offset
        }} transition={{
          duration: 1.5,
          ease: 'easeOut'
        }} cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={percentage >= 100 ? '#10B981' : '#F59E0B'} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeLinecap="round" data-id="element-1327" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-1328">
          <span className="text-2xl font-black text-slate-800 leading-none" data-id="element-1329">
            {answered}
          </span>
          <span className="text-xs font-bold text-slate-400 border-t-2 border-slate-100 pt-0.5 mt-0.5 w-8 text-center" data-id="element-1330">
            {goal}
          </span>
        </div>
      </div>

      <p className={`font-bold ${percentage >= 100 ? 'text-emerald-600' : 'text-amber-600'} relative z-10`} data-id="element-1331">
        {message}
      </p>
    </Card>;
}
function WeekStreakCard({
  streak,
  weeklyActivity,
  weeklyGoal
}: {
  streak: number;
  weeklyActivity: DayActivity[];
  weeklyGoal: number;
}) {
  return <Card className="p-6 border-2 border-orange-100 bg-white rounded-[2rem] flex flex-col justify-between relative overflow-hidden shadow-md hover:shadow-lg transition-shadow" data-id="element-1332">
      <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-orange-50 rounded-full blur-2xl" data-id="element-1333" />

      <div className="flex justify-between items-start mb-6 relative z-10" data-id="element-1334">
        <div data-id="element-1335">
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-1336">
            Weekdoel
          </h3>
          <div className="flex items-baseline gap-2" data-id="element-1337">
            <span className="text-4xl font-black text-slate-800" data-id="element-1338">{streak}</span>
            <span className="text-sm font-bold text-slate-500" data-id="element-1339">
              / {weeklyGoal} dagen
            </span>
          </div>
        </div>
        <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center shadow-sm" data-id="element-1340">
          <StreakFlame streak={streak} size="md" data-id="element-1341" />
        </div>
      </div>

      <div className="relative z-10" data-id="element-1342">
        <WeeklyActivityDots weeklyActivity={weeklyActivity} color="emerald" weeklyGoal={weeklyGoal} data-id="element-1343" />
      </div>
    </Card>;
}